import React, { useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { components, OptionProps, SingleValueProps } from "react-select";
import { PlatformContext } from "@upptic/module-directory";
import { observer } from "mobx-react";

import iosImage from "assets/images/ios_color.svg";
import androidImage from "assets/images/android_color.svg";

import { ApplicationContext } from "contexts";

import { SelectInput } from "components/forms";

// -- COMPONENT
const ApplicationPlatformSwitcher: React.FC = () => {
  const applicationCtx = useContext(ApplicationContext);
  const platformStore = useContext(PlatformContext);
  const history = useHistory();
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  useEffect(() => {
    setSelectedOption(platformStore.selectedAppPlatform?.code || null);
  }, [setSelectedOption, platformStore.selectedAppPlatform]);

  const apps: any[] = useMemo(() => {
    const applicationList = [...platformStore.applications]
      .sort((a: any, b: any) => {
        const aName = a.name?.toLowerCase();
        const bName = b.name?.toLowerCase();

        if (aName > bName) {
          return 1;
        }

        if (aName < bName) {
          return -1;
        }

        return 0;
      })
      .reduce((acc, application: any) => {
        if (!application.appPlatform) return acc;

        const appPlatforms = application.appPlatform
          .filter(({ asoAppId }) => asoAppId)
          .map((platform) => ({
            ...platform,
            name: application.name,
          }));
        acc.push(...appPlatforms);
        return acc;
      }, [] as any[]);

    if (!selectedOption) {
      return applicationList;
    }

    const selectedOptionFromList = applicationList.find(
      (singleApplication) => singleApplication.code === selectedOption,
    );

    if (!selectedOptionFromList) {
      return applicationList;
    }

    const correctApplicationList = applicationList.filter(
      (singleApp) => singleApp.asoAppId !== selectedOption,
    );

    return [selectedOptionFromList, ...correctApplicationList];
  }, [platformStore.applications, selectedOption]);

  const onChangeApplication = (code: string) => {
    const selectedNewApp = apps.find(
      (singleAppGroup) => singleAppGroup.code === code,
    );
    const pathname = history.location.pathname;

    if (!selectedNewApp) {
      return false;
    }

    platformStore.setSelectedAppPlatform(selectedNewApp);
    setSelectedOption(selectedNewApp.code);
  };

  const SingleValue = (props: SingleValueProps<{ [key: string]: any }>) => {
    return (
      <components.SingleValue {...props}>
        <BrandIcon brandType={props.data.platform} />
        {props.data.name}
      </components.SingleValue>
    );
  };

  const Option = (props: OptionProps<{ [key: string]: any }, boolean>) => {
    return (
      <components.Option {...props}>
        <OptionContenWrapper>
          <BrandIcon brandType={props.data.platform} />
          {props.data.name}
        </OptionContenWrapper>
      </components.Option>
    );
  };

  if (apps.length < 2) {
    return null;
  }

  return (
    <Wrapper>
      <SelectInput
        labelKey={"name"}
        valueKey={"code"}
        value={selectedOption}
        options={apps}
        onChange={onChangeApplication}
        components={{ Option, SingleValue }}
      />
    </Wrapper>
  );
};

// -- STYLED
const Wrapper = styled.div`
  position: relative;
  width: 200px;
`;

const BrandIcon = styled.div<{ brandType: string }>`
  background-image: url(${(props) =>
    props.brandType === "ios" ? iosImage : androidImage});
  background-size: ${(props: any) =>
    props.brandType === "ios" ? "30px" : "25px"};
  height: 25px;
  width: 25px;
  float: left;
  margin-right: 5px;
  margin-left: 2px;
`;

const OptionContenWrapper = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
`;

export default observer(ApplicationPlatformSwitcher);
